import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { usePortal } from '../../context/PortalContext';
import { useToast } from '../common/Toast';
import { loadGoogleSDK, loadMicrosoftSDK, loadAppleSDK } from '../../utils/loadAuthSDK';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || (window.location.origin + '/auth/apple/callback');

// The backend requires a verified email before creating a portal account, so
// registration is two steps: collect details and send a code, then enter it.
const RESEND_COOLDOWN_SECONDS = 60;

const PortalRegisterModal = ({ isOpen, onClose, onSwitchToLogin, onSuccess }) => {
  const { portalRegister, portalSendOtp, portalVerifyOtp, portalGoogleSignIn, portalMicrosoftSignIn, portalAppleSignIn } = usePortal();
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState('details'); // 'details' | 'verify'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const googleButtonRef = React.useRef(null);
  const googleTimeoutRef = React.useRef(null);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setStep('details');
      setName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setOtp('');
      setResendIn(0);
    } else if (googleTimeoutRef.current) {
      clearTimeout(googleTimeoutRef.current);
      googleTimeoutRef.current = null;
    }
  }, [isOpen]);

  // Resend countdown so users don't trip the backend's 60s per-email cooldown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    return () => {
      if (googleTimeoutRef.current) {
        clearTimeout(googleTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const sendCode = async () => {
    const result = await portalSendOtp(email, name);
    if (!result.success) {
      setError(result.error);
      return false;
    }
    setResendIn(RESEND_COOLDOWN_SECONDS);
    return true;
  };

  const handleResend = async () => {
    if (resendIn > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      await sendCode();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 'details') {
      if (!name.trim()) {
        setError('Please enter your name');
        return;
      }
      if (!email.trim()) {
        setError('Please enter your email');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      setLoading(true);
      try {
        if (await sendCode()) setStep('verify');
      } catch (err) {
        setError(err?.message || 'Could not send the verification code. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Verify the code, then create the account with it.
    if (otp.trim().length !== 6) {
      setError('Enter the 6-digit code we emailed you');
      return;
    }

    setLoading(true);
    try {
      const verified = await portalVerifyOtp(email, otp.trim());
      if (!verified.success) {
        setError(verified.error);
        return;
      }
      const result = await portalRegister(name, email, password, otp.trim());
      if (result.success) {
        toast.success(`Welcome aboard, ${name.split(' ')[0]}! Your account has been created.`);
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google sign-in is not configured');
      return;
    }
    setGoogleLoading(true);
    setError('');

    try {
      await loadGoogleSDK();
    } catch {
      setError('Google Identity library failed to load');
      setGoogleLoading(false);
      return;
    }
    if (!window.google) {
      setError('Google Identity library not loaded');
      setGoogleLoading(false);
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        try {
          const result = await portalGoogleSignIn(response.credential);
          if (result.success) {
            if (onSuccess) {
              // Caller wants to continue (e.g. into the subscription panel)
              onSuccess();
            } else {
              onClose();
              if (result.portal_user?.can_access_app) {
                toast.success('Signed in! Redirecting to the app…');
                navigate('/login', { state: { googleCredential: response.credential } });
              } else {
                toast.success('Signed in with Google successfully!');
              }
            }
          } else {
            setError(result.error || 'Google sign-in failed');
          }
        } catch {
          setError('Google sign-in failed');
        } finally {
          setGoogleLoading(false);
        }
      },
    });

    if (googleButtonRef.current) {
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        size: 'large',
        width: 0,
      });
      setTimeout(() => {
        const btn = googleButtonRef.current?.querySelector('div[role="button"]') || googleButtonRef.current?.querySelector('button');
        if (btn) {
          btn.click();
        } else {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment() || notification.isDismissedMoment()) {
              setGoogleLoading(false);
              setError('Google sign-in could not be displayed. Please try email sign-in.');
            }
          });
        }
      }, 100);
    } else {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment() || notification.isDismissedMoment()) {
          setGoogleLoading(false);
          setError('Google sign-in could not be displayed. Please try email sign-in.');
        }
      });
    }

    googleTimeoutRef.current = setTimeout(() => {
      setGoogleLoading(false);
    }, 30000);
  };

  const handleMicrosoftSignIn = async () => {
    if (!MICROSOFT_CLIENT_ID) {
      setError('Microsoft sign-in is not configured');
      return;
    }
    setMicrosoftLoading(true);
    setError('');
    try {
      try {
        await loadMicrosoftSDK();
      } catch {
        setError('Microsoft authentication library failed to load');
        setMicrosoftLoading(false);
        return;
      }
      if (!window.msal) {
        setError('Microsoft authentication library not loaded');
        setMicrosoftLoading(false);
        return;
      }
      const msalConfig = {
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: window.location.origin,
        },
      };
      const msalInstance = new window.msal.PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup({
        scopes: ['openid', 'email', 'profile'],
      });
      const idToken = loginResponse.idToken || loginResponse.accessToken;
      if (!idToken) {
        setError('Microsoft sign-in did not return a token');
        setMicrosoftLoading(false);
        return;
      }
      const result = await portalMicrosoftSignIn(idToken);
      if (result.success) {
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
          toast.success('Signed in with Microsoft successfully!');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || 'Microsoft sign-in failed');
    } finally {
      setMicrosoftLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (!APPLE_CLIENT_ID) {
      setError('Apple sign-in is not configured');
      return;
    }
    setAppleLoading(true);
    setError('');
    try {
      try {
        await loadAppleSDK();
      } catch {
        setError('Apple sign-in library failed to load');
        setAppleLoading(false);
        return;
      }
      if (!window.AppleID) {
        setError('Apple sign-in library not loaded');
        setAppleLoading(false);
        return;
      }
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const idToken = response.authorization?.id_token;
      if (!idToken) {
        setError('Apple sign-in did not return a token');
        setAppleLoading(false);
        return;
      }
      const result = await portalAppleSignIn(idToken);
      if (result.success) {
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
          toast.success('Signed in with Apple successfully!');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || 'Apple sign-in failed');
    } finally {
      setAppleLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="modal-backdrop fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="modal-content relative bg-white dark:bg-gray-800 mx-auto rounded-xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700" style={{ maxWidth: '500px', width: '100%' }}>
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Create your account</h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md p-1.5 transition-all"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Body */}
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {error && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {step === 'details' && (<>
              {/* Social Sign-In Buttons */}
              <div className="flex flex-col items-center gap-2.5">
                {/* Hidden container for GIS renderButton */}
                <div ref={googleButtonRef} style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true"></div>
                {/* Google */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || loading}
                  title="Continue with Google"
                  className="mx-auto flex items-center justify-center gap-2 py-1.5 px-5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-[11px] font-medium text-gray-700 dark:text-gray-200 transition-all disabled:opacity-50"
                >
                  {googleLoading ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">Google</span>
                </button>

                {/* Microsoft — hidden until MICROSOFT_CLIENT_ID is configured */}
                {MICROSOFT_CLIENT_ID && (
                <button
                  type="button"
                  onClick={handleMicrosoftSignIn}
                  disabled={microsoftLoading || loading}
                  title="Continue with Microsoft"
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 transition-all disabled:opacity-50"
                >
                  {microsoftLoading ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 23 23">
                      <path fill="#f25022" d="M1 1h10v10H1z" />
                      <path fill="#7fba00" d="M12 1h10v10H12z" />
                      <path fill="#00a4ef" d="M1 12h10v10H1z" />
                      <path fill="#ffb900" d="M12 12h10v10H12z" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">Microsoft</span>
                </button>
                )}

                {/* Apple — hidden until APPLE_CLIENT_ID is configured */}
                {APPLE_CLIENT_ID && (
                <button
                  type="button"
                  onClick={handleAppleSignIn}
                  disabled={appleLoading || loading}
                  title="Continue with Apple"
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 transition-all disabled:opacity-50"
                >
                  {appleLoading ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">Apple</span>
                </button>
                )}
              </div>

              <div className="relative flex items-center my-3">
                <div className="flex-grow border-t border-gray-200 dark:border-gray-600" />
                <span className="px-3 text-xs text-gray-400">or sign up with email</span>
                <div className="flex-grow border-t border-gray-200 dark:border-gray-600" />
              </div>

              {/* Form fields with input-rx styling */}
              <div className="space-y-3">
                <div>
                  <label className="label-rx">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="input-rx text-sm"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="label-rx">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-rx text-sm"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="label-rx">Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="input-rx text-sm pr-7"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label-rx">Confirm Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      className="input-rx text-sm pr-7"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              </>)}

              {/* Step 2 — verify the emailed code */}
              {step === 'verify' && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900 dark:border-teal-900/50 dark:bg-teal-900/20 dark:text-teal-200">
                    We emailed a 6-digit code to <span className="font-semibold">{email}</span>. Enter it below to finish creating your account.
                  </div>
                  <div>
                    <label className="label-rx">Verification code <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                      required
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      className="input-rx text-center text-2xl font-bold tracking-[0.5em] placeholder:text-base placeholder:tracking-normal placeholder:text-gray-300 dark:placeholder:text-gray-600"
                      placeholder="000000"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => { setStep('details'); setOtp(''); setError(''); }}
                      className="font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400"
                    >
                      Change details
                    </button>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendIn > 0 || loading}
                      className="font-medium text-teal-600 hover:text-teal-700 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-teal-400"
                    >
                      {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50/50 dark:bg-gray-900/50 px-5 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => { onClose(); onSwitchToLogin(); }}
                className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
              >
                Already have an account? Sign in
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {loading
                  ? (step === 'verify' ? 'Creating…' : 'Sending…')
                  : (step === 'verify' ? 'Verify & Create Account' : 'Send verification code')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PortalRegisterModal;
